<template>
  <div class="rich-text-editor">
    <Editor
      v-bind="$attrs"
      :id="identifier"
      ref="editor"
      licence-key="gpl"
      :init="getConfig"
      :initial-value="placeholder"
      :value="originalContent"
      :menubar="menubar"
      :model-value="content"
      :disabled="disabled"
      @update:model-value="update"
    />
    <AksPlugins
      v-if="currentEditor"
      :id="identifier"
      :editor="currentEditor"
      :plugin-manager="tinymce.PluginManager"
      :use-media-library-service="useMediaLibraryService"
      :use-tracking-service="useTrackingService"
      :image-cdn-service="imageCdnService"
      :use-drawer-service="useDrawerService"
      :capture-error="captureError"
      @all-content-updated="emit('allContentUpdated', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import Editor from '@tinymce/tinymce-vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { internalRichTextEditorProps } from './props';
import { useAksPluginManager, AksPlugins } from './plugins';

const props = defineProps(internalRichTextEditorProps);
const { identifier, useMediaLibraryService, useTrackingService, imageCdnService, useDrawerService, captureError } = toRefs(props);
const { injectAksPluginConfig } = useAksPluginManager(identifier.value);

const emit = defineEmits(['contentUpdated', 'allContentUpdated']);

const content = ref(props.originalContent ?? props.placeholder ?? '');

const editor = ref<typeof Editor>(null);

const currentEditor = computed(() => {
  return editor.value?.getEditor();
});

const getConfig = computed(() => injectAksPluginConfig({
  height: props.height,
  menubar: props.menubar,
  relative_urls: false,
  remove_script_host: false,
  plugins: [
    'link',
    'advlist',
    'lists',
    'emoticons',
  ],
  toolbar: ['undo redo | bold italic underline | alignleft aligncenter alignright | forecolor emoticons'],
  skin: false,
  content_style:
    '@import url(https://fonts.googleapis.com/css2?family=Poppins:wght@400;700&display=swap); body { font-family: Poppins; }',
}));

watch(
  () => props.originalContent,
  (val) => {
    content.value = val ?? props.placeholder;
  }
);

const update = function (value: string) {
  content.value = value;
  emit('contentUpdated', value);
};

const focusinTox = function (event: FocusEvent) {
  //based on tiny.cloud/docs/integrations/bootstrap/
  if (
    window.jQuery(event.target).closest('.tox-tinymce, .tox-dialog, .tox-tinymce-aux, .moxman-window, .tam-assetmanager-root')
      .length
  ) {
    event.stopImmediatePropagation();
  }
};

const setContent = (value: string) => {
  content.value = value ?? props.placeholder;
};

onMounted(() => {
  document.addEventListener('focusin', focusinTox, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('focusin', focusinTox, true);
});

defineExpose({
  setContent,
});

/* Import TinyMCE */
import { toRefs } from 'vue';
import { TinyMCE } from 'tinymce';
const tinymce = (await import('tinymce')) as unknown as TinyMCE;
globalThis.tinymce ??= tinymce;

await Promise.all([
  /* Default icons are required. After that, import custom icons if applicable */
  import('tinymce/icons/default/icons.min.js'),

  /* Required TinyMCE components */
  import('tinymce/themes/silver/theme.min.js'),
  import('tinymce/models/dom/model.min.js'),

  /* Import a skin (can be a custom skin instead of the default) */
  import('tinymce/skins/ui/oxide/skin.js'),
  import('tinymce/skins/ui/oxide/content.js'),
  import('tinymce/skins/content/default/content.js'),

  /* Import standard plugins */
  import('tinymce/plugins/advlist'),
  import('tinymce/plugins/emoticons'),
  import('tinymce/plugins/emoticons/js/emojis'),
  import('tinymce/plugins/link'),
  import('tinymce/plugins/lists'),
]);
</script>

<style lang="scss">
/* stylelint-disable */
@import 'node_modules/tinymce/skins/ui/oxide/skin.min';
@import 'node_modules/tinymce/skins/ui/oxide/content.min';
@import 'node_modules/tinymce/skins/content/default/content.min';
/* stylelint-enable */
</style>
